/**
 * Juridische pagina's. Opgesteld als heldere basis op basis van de werkelijke
 * afspraken in `policies.ts` — laat ze vóór livegang controleren door een jurist
 * (of vergelijk met het Thuiswinkel-model).
 */
import { policy, policyCopy } from './policies'
import { founder, salon } from './content'
import { euro } from '../core/format/currency'

/** `id`: vast ankertje om vanaf de site naar één clausule te linken (bijv. `#reviews`). */
export type LegalSection = { heading: string; body: string[]; id?: string }

/** De clausule over hoe reviews gecontroleerd worden (Omnibus); de reviews op de productpagina linken hierheen. */
export const REVIEWS_CLAUSE = '/algemene-voorwaarden#reviews'

export type LegalDoc = {
  path: string
  title: string
  seoTitle: string
  description: string
  updated: string
  intro: string
  sections: LegalSection[]
}

const UPDATED = '5 oktober 2026'

const buildLegalDocs = (): LegalDoc[] => [
  {
    path: '/verzending-en-retour',
    title: 'Verzending & retour',
    seoTitle: 'Verzending & retour | Beauty Bewust',
    description: `${policyCopy.freeShipping}, ${policyCopy.delivery.toLowerCase()} en ${policyCopy.returns}. Zo werkt bestellen bij Beauty Bewust.`,
    updated: UPDATED,
    intro:
      'Ik pak elke bestelling zelf in, met een handgeschreven advies erbij. Hieronder lees je precies wat je van de verzending en het ruilen kunt verwachten.',
    sections: [
      {
        heading: 'Verzendkosten',
        body: [
          `Vanaf ${euro(policy.freeShippingFrom)} verzend ik gratis binnen Nederland en België. ${policyCopy.shippingBelow}.`,
          'Met een Bewust Abonnement is verzending altijd gratis, ongeacht het bedrag.',
        ],
      },
      {
        heading: 'Levertijd',
        body: [
          `Op werkdagen voor ${policy.orderCutoff} besteld betekent de volgende werkdag in huis binnen Nederland. Naar België duurt het één werkdag langer.`,
          'Je ontvangt een track & trace zodra je pakket onderweg is.',
        ],
      },
      {
        heading: `${policy.returnDays} dagen ruilen als het niet bij je huid past`,
        body: [
          `Past een product toch niet bij je huid? Stuur binnen ${policy.returnDays} dagen na ontvangst een appje met wat je huid doet. Ik zoek met je een alternatief en je ruilt het product om, ook als het al geopend is. Je betaalt alleen een eventueel prijsverschil.`,
        ],
      },
      {
        heading: 'Herroepingsrecht (14 dagen)',
        body: [
          'Ongeopende producten mag je binnen 14 dagen na ontvangst zonder opgave van reden retourneren. Meld dit via mail of WhatsApp; je krijgt het aankoopbedrag inclusief de oorspronkelijke verzendkosten binnen 14 dagen terug.',
          'Om hygiënische redenen geldt het herroepingsrecht niet voor geopende verzorgingsproducten. Daarvoor is de ruilregeling hierboven bedoeld.',
          'De kosten voor het terugsturen zijn voor jouw rekening.',
          'Dit is je wettelijke bedenktijd. De ruilregeling hierboven komt daar bovenop en vervangt hem niet.',
        ],
      },
      {
        heading: 'Modelformulier voor herroeping',
        body: [
          `Je mag dit formulier gebruiken, maar een appje of mail met dezelfde gegevens is ook goed. Stuur het naar ${salon.email}.`,
          `Aan: ${salon.name}, ${salon.address}, ${salon.email}.`,
          'Ik deel hierbij mede dat ik onze overeenkomst betreffende de verkoop van de volgende producten herroep: [product(en)].',
          'Besteld op / ontvangen op: [datum]. Bestelnummer: [nummer].',
          'Naam en adres: [jouw naam en adres]. Datum: [datum].',
        ],
      },
      {
        heading: 'Beschadigd of verkeerd ontvangen?',
        body: [
          'Laat het me binnen twee dagen weten met een foto. Ik stuur dan kosteloos een nieuw product.',
        ],
      },
    ],
  },
  {
    path: '/algemene-voorwaarden',
    title: 'Algemene voorwaarden',
    seoTitle: 'Algemene voorwaarden | Beauty Bewust',
    description:
      'De algemene voorwaarden van Beauty Bewust voor bestellingen in de webshop, het Bewust Abonnement en afspraken in de salon.',
    updated: UPDATED,
    intro: `Deze voorwaarden gelden voor alle bestellingen in de webshop en afspraken bij ${salon.name}, gevestigd aan de ${salon.address}, KvK ${salon.kvk}${salon.vatId ? `, btw-id ${salon.vatId}` : ''}.`,
    sections: [
      {
        heading: 'Prijzen en betaling',
        body: [
          'Alle prijzen zijn in euro’s en inclusief btw. Ik hanteer de adviesprijzen van de merken.',
          `Je betaalt veilig met ${policyCopy.payments}. Een bestelling is definitief zodra de betaling is ontvangen.`,
        ],
      },
      {
        heading: 'Cadeaus bij je bestelling',
        body: [
          'Cadeaus uit de cadeaustaffel worden automatisch toegevoegd op basis van het bedrag in je winkelmand. Ze zijn niet inwisselbaar voor geld en vervallen als je bestelling door retour onder de drempel komt. Bijvuldozen van het Bewust Abonnement tellen niet mee voor de cadeaustaffel; daar zit op vaste momenten een eigen cadeau bij.',
        ],
      },
      {
        heading: 'Bewust Abonnement',
        body: [
          `Je kiest zelf het ritme van je leveringen. Aanpassen, pauzeren of stoppen kan altijd, zonder opzegtermijn, ${policyCopy.changeUntil}.`,
        ],
      },
      {
        heading: `Online huidconsult (${policy.consult.name})`,
        body: [
          `Het consult kost ${euro(policy.consult.price)} en wordt vooraf betaald. Bestel je binnen ${policy.consult.creditDays} dagen daarna voor ${euro(policy.consult.creditFrom)} of meer, dan verreken ik het consult volledig met die bestelling.`,
        ],
      },
      {
        heading: 'Afspraken in de salon',
        body: [
          `Je eerste behandeling reken je vooraf online af. Kun je niet komen? Verzet of annuleer uiterlijk ${policy.cancelHours} uur van tevoren; daarna breng ik de behandeling in rekening.${policy.noShowUsesSession ? ' Heb je een kuur en kom je zonder afmelden niet, dan telt die sessie als gebruikt.' : ''}`,
        ],
      },
      {
        heading: 'Advies',
        body: [
          `Mijn advies is gebaseerd op ${policy.yearsExperience}+ jaar ervaring, maar vervangt geen medisch advies. Bij twijfel over een medische oorzaak verwijs ik je door naar je huisarts of dermatoloog.`,
        ],
      },
      {
        heading: 'Reviews',
        id: 'reviews',
        body: [
          'Een review kan alleen worden geplaatst door iemand die het product bij mij heeft gekocht of de behandeling heeft gehad. Ik controleer dat aan de hand van de bestelling of afspraak. Ik plaats positieve én kritische reviews en pas de tekst niet aan.',
        ],
      },
      {
        heading: 'Klachten',
        body: [
          `Ben je ergens niet tevreden over? Laat het me weten via ${salon.email} of WhatsApp. Ik reageer ${policyCopy.response.toLowerCase()} en zoek samen met je naar een oplossing.`,
        ],
      },
    ],
  },
  {
    path: '/privacy',
    title: 'Privacybeleid',
    seoTitle: 'Privacybeleid | Beauty Bewust',
    description:
      'Hoe Beauty Bewust omgaat met je persoonsgegevens: welke gegevens ik bewaar, waarom, en hoe je ze kunt inzien of laten verwijderen.',
    updated: UPDATED,
    intro: `Ik ga zorgvuldig om met je gegevens. ${salon.name} (${founder.name}, KvK ${salon.kvk}) is verantwoordelijk voor de verwerking die hieronder staat beschreven.`,
    sections: [
      {
        heading: 'Welke gegevens ik bewaar',
        body: [
          'Voor bestellingen: je naam, adres, e-mailadres en telefoonnummer. Voor afspraken en huidadvies: wat je me vertelt over je huid en eventuele foto’s die je stuurt.',
          'Betaalgegevens worden verwerkt door de betaalprovider; die zie ik zelf niet.',
        ],
      },
      {
        heading: 'Waarvoor',
        body: [
          'Om je bestelling te versturen, je afspraak te plannen en je persoonlijk te adviseren. Heb je aangegeven een seintje te willen als je product bijna op is, dan stuur ik je één bericht per product.',
          'Ik stuur alleen een nieuwsbrief als je je daar zelf voor hebt aangemeld. Afmelden kan onderaan elke mail.',
          'Bij de nieuwsbrief zie ik via de maildienst of een mail geopend is en op welke links geklikt is. Zo weet ik welke onderwerpen je interesseren. Dat koppel ik niet aan advertenties en deel ik met niemand. Afmelden stopt het meteen.',
          'Krijg je de nieuwsbrief en ben je ingelogd, dan onthoud ik je winkelmand, zodat ik je er één keer aan kan herinneren als je hem laat staan. Je verjaardag (alleen dag en maand) gebruik ik alleen als je die zelf invult, voor een felicitatie.',
        ],
      },
      {
        heading: 'Hoe lang',
        body: [
          'Bestelgegevens bewaar ik zeven jaar, vanwege de wettelijke bewaarplicht. Huidinformatie en foto’s verwijder ik op verzoek direct, en anders twee jaar na je laatste contact.',
        ],
      },
      {
        heading: 'Delen met anderen',
        body: [
          'Alleen met partijen die nodig zijn om je bestelling en afspraken af te handelen: de hosting van de webshop (waar ook de bezoekersstatistiek draait), de betaalprovider (Mollie), de vervoerder en de maildienst (Brevo, met servers in de EU) voor bevestigingen, herinneringen en, als je je hebt aangemeld, de nieuwsbrief. Met elk van hen heb ik een verwerkersovereenkomst. Ik verkoop je gegevens nooit.',
        ],
      },
      {
        heading: 'Jouw rechten',
        body: [
          `Je mag je gegevens altijd inzien, laten aanpassen of laten verwijderen. Met een account doe je dat zelf in Mijn omgeving → Gegevens: daar download je alles wat ik bewaar of verwijder je je account. Zonder account mail je naar ${salon.email}. Een klacht kun je ook indienen bij de Autoriteit Persoonsgegevens.`,
        ],
      },
      {
        heading: 'Hoe kwam je bij ons?',
        body: [
          'Na een bestelling, bij een eerste afspraak of in de salon vraag ik soms hoe je bij me terechtkwam (bijvoorbeeld via Instagram of via iemand). Antwoorden hoeft nooit. Doe je het wel, dan bewaar ik alleen je eerste antwoord bij je gegevens, om te zien waar ik mijn tijd het best in steek. Ik gebruik het niet voor advertenties en deel het met niemand.',
        ],
      },
      {
        heading: 'Bezoekers tellen, zonder cookies',
        body: [
          'Om te zien welke pagina’s gelezen worden en waar bezoekers vandaan komen, tel ik bezoeken met Umami. Dat draait op mijn eigen server in de EU, zet geen cookies en slaat geen IP-adressen op. Een bezoeker wordt alleen binnen één dag herkend en nooit gevolgd over andere sites. Ik zie dus aantallen, nooit wie jij bent.',
          'Ik tel ook een paar handelingen: iets in je winkelmand leggen, beginnen met afrekenen, een afspraak aanvragen, het huidadvies afronden, je aanmelden voor de nieuwsbrief, en wat er in de webshop gezocht wordt (zonder e-mailadressen, telefoon- of ordernummers). Op de pagina’s voor afrekenen, je bestelling en Mijn omgeving tel ik niets.',
          'Staat Do Not Track of Global Privacy Control aan in je browser, dan tel ik je helemaal niet.',
        ],
      },
      {
        heading: 'Cookies',
        body: [
          'We volgen je niet. De site gebruikt alleen functionele opslag in je browser, bijvoorbeeld om je winkelmand te onthouden of je ingelogd te houden. Er zijn geen tracking- of marketingcookies, de bezoekersstatistiek werkt zonder cookies (zie hierboven) en de lettertypen worden zelf gehost, dus er gaat niets naar Google of Meta. (Over het meten bij de nieuwsbrief: zie hierboven bij "Waarvoor".)',
          'Komen er ooit marketingcookies bij, dan vraag ik eerst je toestemming.',
        ],
      },
    ],
  },
]

/** Tekst staat hier, feiten komen uit de instellingen. Bijgewerkt via `rebuildLegal()`. */
export const legalDocs: LegalDoc[] = []

export function rebuildLegal() {
  legalDocs.splice(0, legalDocs.length, ...buildLegalDocs())
}

rebuildLegal()

export const legalByPath = (path: string) => legalDocs.find((d) => d.path === path)

export const legalLinks = {
  shipping: '/verzending-en-retour',
  terms: '/algemene-voorwaarden',
  privacy: '/privacy',
} as const
